import type { RuntimeTheme } from './types'

/**
 * Applies theme tokens to the current document root.
 *
 * @param theme Runtime theme payload to apply.
 * @param options Persistence controls for storing applied theme.
 */
export const applyRuntimeThemeToDocument = (
  theme: RuntimeTheme,
  options?: { persist?: boolean; persistTheme?: (value: RuntimeTheme) => void },
) => {
  if (typeof document === 'undefined') return
  const root = document.documentElement

  root.setAttribute('data-theme', theme.mode)
  root.classList.toggle('theme-dark', theme.mode === 'dark')
  root.classList.toggle('theme-light', theme.mode !== 'dark')

  root.style.setProperty('--theme-color-primary', theme.tokens.colors.primary)
  root.style.setProperty('--theme-color-background', theme.tokens.colors.background)
  root.style.setProperty('--theme-color-surface', theme.tokens.colors.surface)
  root.style.setProperty('--theme-color-text', theme.tokens.colors.text)
  root.style.setProperty('--theme-color-muted', theme.tokens.colors.muted)
  root.style.setProperty('--theme-color-border', theme.tokens.colors.border)
  root.style.setProperty('--theme-color-accent', theme.tokens.colors.accent)

  root.style.setProperty('--theme-font-family-base', theme.tokens.typography.fontFamilyBase)
  root.style.setProperty('--theme-font-family-heading', theme.tokens.typography.fontFamilyHeading)
  root.style.setProperty('--theme-font-scale-base', String(theme.tokens.typography.fontScaleBase))

  root.style.setProperty('--theme-radius-sm', theme.tokens.radius.sm)
  root.style.setProperty('--theme-radius-md', theme.tokens.radius.md)
  root.style.setProperty('--theme-radius-lg', theme.tokens.radius.lg)
  root.style.setProperty('--theme-radius-xl', theme.tokens.radius.xl)

  root.style.setProperty('--theme-spacing-xs', theme.tokens.spacing.xs)
  root.style.setProperty('--theme-spacing-sm', theme.tokens.spacing.sm)
  root.style.setProperty('--theme-spacing-md', theme.tokens.spacing.md)
  root.style.setProperty('--theme-spacing-lg', theme.tokens.spacing.lg)
  root.style.setProperty('--theme-spacing-xl', theme.tokens.spacing.xl)

  root.style.colorScheme = theme.mode === 'dark' ? 'dark' : 'light'

  if (options?.persist !== false) {
    options?.persistTheme?.(theme)
  }
}
